export const DASHBOARD_LAYOUT_VERSION = 3;

export const DASHBOARD_LAYOUT_ZONES = ["miniStats", "splitRow", "fullWidth"] as const;
export type DashboardLayoutZone = (typeof DASHBOARD_LAYOUT_ZONES)[number];

export const DASHBOARD_SPLIT_SLOTS = ["left", "right"] as const;
export type DashboardSplitSlot = (typeof DASHBOARD_SPLIT_SLOTS)[number];

export const DASHBOARD_CARD_TYPES = [
  "portfolio",
  "expectancy",
  "winRate",
  "monthlyPnL",
  "roi",
  "closedTrades",
  "activeTrades",
  "largestWin",
  "averageWin",
  "largestLoss",
  "averageLoss",
  "portfolioGrowth",
  "winRateTrend",
  "pnlMilestones",
  "tradeActivityCalendar",
  "pnlOverTime",
  "tradeDistribution",
  "performanceBreakdown",
  "streakAnalysis",
  "longVsShortPerformance",
  "keyRiskMetrics",
  "drawdownOverTime",
  "equityPeakPath",
] as const;
export type DashboardCardType = (typeof DASHBOARD_CARD_TYPES)[number];

export interface DashboardLayoutItem {
  cardType: DashboardCardType;
  visible: boolean;
  zone: DashboardLayoutZone;
  order?: number;
  slot?: DashboardSplitSlot;
  ratio?: number;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
}

export interface DashboardLayout {
  id: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  layoutVersion: number;
  items: DashboardLayoutItem[];
  createdAt?: string;
  updatedAt?: string;
}

export interface DashboardLayoutState {
  layouts: DashboardLayout[];
  activeLayoutId: string;
  isEditMode: boolean;
  draftLayout?: DashboardLayout | null;
  isHydrating: boolean;
  persistenceError: string | null;
  pendingMutation: DashboardLayoutMutationType | null;
  isMutating: boolean;
}

export type DashboardLayoutMutationType =
  | "setActiveLayout"
  | "createLayout"
  | "abandonNewLayout"
  | "renameLayout"
  | "duplicateLayout"
  | "deleteLayout"
  | "setEditMode"
  | "updateLayoutItems"
  | "addCard"
  | "removeCard"
  | "resetToDefault"
  | "saveEditModeChanges"
  | "cancelEditModeChanges";

export interface DashboardLayoutActions {
  setActiveLayout: (layoutId: string, options?: { preserveEditorReset?: boolean }) => void;
  createLayout: (name: string, options?: { openEditor?: boolean }) => string;
  abandonNewLayout: (layoutId: string, fallbackLayoutId?: string) => void;
  renameLayout: (layoutId: string, name: string) => void;
  duplicateLayout: (layoutId: string) => string | null;
  deleteLayout: (layoutId: string) => void;
  setEditMode: (enabled: boolean, layoutId?: string) => void;
  updateLayoutItems: (layoutId: string, items: DashboardLayoutItem[]) => void;
  addCard: (
    layoutId: string,
    cardType: DashboardCardType,
    targetZone?: DashboardLayoutZone
  ) => void;
  removeCard: (layoutId: string, cardType: DashboardCardType) => void;
  resetToDefault: (layoutId?: string) => void;
  saveEditModeChanges: () => void;
  cancelEditModeChanges: () => void;
}

export interface DashboardLayoutSelectors {
  getActiveLayout: () => DashboardLayout | null;
  getLayoutItems: (layoutId?: string) => DashboardLayoutItem[];
  getVisibleItems: (layoutId?: string) => DashboardLayoutItem[];
  canDeleteLayout: (layoutId: string) => boolean;
}

export interface DashboardLayoutStore extends DashboardLayoutState, DashboardLayoutActions, DashboardLayoutSelectors {}

export interface DashboardCardRegistryEntry {
  cardType: DashboardCardType;
  title: string;
  description?: string;
  removable: boolean;
  compatibleZones: DashboardLayoutZone[];
  defaultItem: DashboardLayoutItem;
}
